import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { faCircleInfo, faFile, faGlobe, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formValidation, notEmpty } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import AppModal from "../../components/common/AppModal";
import { IJSTreeNode, IPageMetaData } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import GenericTreeView, { INodeTypeDefinition } from "../../components/common/GenericTreeView";
import Loading from "../../components/common/Loading";
import { useConfirm } from "../../../config/ConfirmProvider";
import { MetaDataService } from "../../../services/MetaDataService";
import { useParams } from "react-router-dom";
import { EventBus } from "../../../helpers/EventBus";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { TreeItemSort } from "../../../config/Enums";
import styles from "./pageMetaData.module.scss";
import AppEmptyState from "../../components/common/AppEmptyState";
import { automaticMetaNote, isAutomaticMetaKey } from "./pageMetadataKeys";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";

// Reusable function for validation configuration
const getValidationConfig = () => ({
  fields: {
    Key: {
      validators: {
        notEmpty: { message: "Meta key is required" },
        stringLength: {
          min: 3,
          max: 50,
          message: "The key must be between 3 and 50 characters long",
        },
      },
    },
    Value: {
      validators: {
        notEmpty: { message: "Meta value is required " }
      }
    }
  },
  plugins: {
    trigger: new Trigger(),
    bootstrap5: new Bootstrap5(),
  },
});

const PageMetaData = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pages, setPages] = useState<Array<IJSTreeNode>>([]);
  const [selectedPage, setSelectedPage] = useState<IJSTreeNode | null>(null);
  const [metaData, setMetaData] = useState<Array<IPageMetaData>>([]);
  const [addMetaData, setAddMetaData] = useState<boolean>(false);
  const [selectedMetaData, setSelectedMetaData] = useState<IPageMetaData | null>(null);

  const contentSvcRef = useRef<ContentService | null>(null);
  const metaSvcRef = useRef<MetaDataService | null>(null);
  const formRefs = useRef<Record<string, HTMLFormElement | null>>({});
  const validatorsRef = useRef<Record<string, any>>({});
  const modalFormRef = useRef<HTMLFormElement | null>(null);

  const confirmation = useConfirm();

  const { pageParamId } = useParams();
  
  // Fetch pages and metadata
  const getPages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const pageres = await contentSvcRef.current.getPagesTreeData();
      if (pageres?.length > 0) {
        setPages(pageres);
      }
    }
  };

  const getMetaData = async (pageid: number): Promise<void> => {
    if (metaSvcRef.current) {
      const mdres = await metaSvcRef.current.getPageMetaData(pageid);
      setMetaData(mdres || []);
    }
  };

  // Set up validators for each main list item
  useEffect(() => {
    metaData.forEach(kw => {
      const formId = `MDFORM-${kw.Id}`;
      if (formRefs.current[formId] && !validatorsRef.current[formId]) {
        validatorsRef.current[formId] = createFormValidation(formRefs.current[formId]!, getValidationConfig());
      }
    });
  }, [metaData]);

  // Set up validator for modal form
  useEffect(() => {
    if (addMetaData && modalFormRef.current && !validatorsRef.current.NEWMETADATA) {
      validatorsRef.current.NEWMETADATA = createFormValidation(modalFormRef.current, getValidationConfig());
    }

    // Cleanup modal validator when modal closes
    return () => {
      if (!addMetaData && validatorsRef.current.NEWMETADATA) {
        validatorsRef.current.NEWMETADATA.destroy();
        delete validatorsRef.current.NEWMETADATA;
      }
    };
  }, [addMetaData]);

  const handlePageSelect = async (selected: IJSTreeNode): Promise<void> => {
    if (selected) {
      setSelectedPage(selected);
    }
  };

  const handleUpdateMeta = (fieldname: string, value: string, mdid: number): void => {
    if (mdid) {
      setMetaData(prev => prev.map(md =>
        md.Id === mdid ? { ...md, [fieldname]: value } : md
      ));
    }
  };

  const addNewMetaData = (): void => {
    if (metaSvcRef.current && selectedPage) {
      const newmd = metaSvcRef.current.newPageMetaData(selectedPage.id);
      if (newmd) {
        setSelectedMetaData(newmd);
        setAddMetaData(true);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedMetaData) {
      const {name, value } = event.target;

      setSelectedMetaData(prev => ({
        ...prev as IPageMetaData,
        [name]: value,
      }));
    }
  };

  const cancelAddMetaData = (): void => {
    setAddMetaData(false);
    setSelectedMetaData(null);
  };

  const rejectAutomaticKey = (key?: string | null): boolean => {
    if (!isAutomaticMetaKey(key)) {
      return false;
    }

    actionEmitter.emit(EmitEvent.Result, false, "Keys starting with sb:auto: are generated from the page content. Add description, title, or another key to keep your own value.");
    return true;
  };

  const saveMetaData = async (id?: number): Promise<void> => {
    if (metaSvcRef.current) {
      if (id) {
        const md = metaData.find(k => k.Id === id);
        if (md) {
          if (isAutomaticMetaKey(md.Key) || rejectAutomaticKey(md.Key)) {
            return;
          }

          const res = await metaSvcRef.current.savePageMetaData(md);
          if (res) {
            setMetaData(prev => prev.map(md => md.Id === res.Id ? res : md));
          }
        }
      } else if (selectedMetaData) {
        if (rejectAutomaticKey(selectedMetaData.Key)) {
          return;
        }

        const newres = await metaSvcRef.current.savePageMetaData(selectedMetaData);
        if (newres) {
          setMetaData(prev => [
            ...prev,
            newres
          ]);
          cancelAddMetaData();
        }
      }
    }
  };

  const deleteMetaData = async (id: number): Promise<void> => {
    if (metaSvcRef.current && selectedPage) {

      const confirmed = await confirmation.requestConfirmation("Delete this meta data entry? <b>This action cannot be undone.</b>", "Delete MetaData");

      if (confirmed) {
        const res = await metaSvcRef.current.deletePageMetaData(id, selectedPage.id);

        if (res) {
          setMetaData(prev => prev.filter(md => md.Id !== id));
        }
      }
    }
  };

  const treeNodeDefinition = (): Array<INodeTypeDefinition> => [
    {
      parentPropertyName: "parent",
      displayPropertyName: "text",
      typeIconDefinition: faFile,
      onItemSelect: handlePageSelect,
      allowDrop: false,
      nodeTypeValue: "page",
    },
    {
      parentPropertyName: "parent",
      displayPropertyName: "text",
      typeIconDefinition: faGlobe,
      onItemSelect: () => console.log("language selected"),
      allowDrop: false,
      nodeTypeValue: "lang",
    },
  ];

  useEffect(() => {
    if (pages.length > 0 && pageParamId && !isNaN(Number(pageParamId))) {
      const page = pages.find(p => p.id.toString() === pageParamId);
      if (page) {
        setSelectedPage(page);
        EventBus.getInstance().publish(EventBus.Event.OnTreeNodeSelect, page.id.toString());
      }
    }
  }, [pages]);

  useEffect(() => {
    if (selectedPage) {
      getMetaData(selectedPage.id);
    }
  }, [selectedPage]);

  useEffect(() => {
    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }
    if (!metaSvcRef.current) {
      metaSvcRef.current = new MetaDataService();
    }
    const getData = async (): Promise<void> => {
      try {
        await getPages();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  return (
    <>
      {loading && <Loading Message="loading page/metadata..." SpinSize="5x" />}
      {!loading && (
        <div className={styles.page}>
          <div className={styles.workspace}>
            <div className={styles.workspaceSidebar}>
              <div className={styles.treePanel}>
                <div className={styles.panelHeader}>
                  <div className={styles.panelTitleWrap}>
                    <h2 className={styles.panelTitle}>Your Site Pages</h2>
                    <p className={styles.panelSubtle}>Choose a page from the site tree to manage its metadata entries.</p>
                  </div>
                </div>
                <div className={styles.panelDivider} aria-hidden="true"></div>
                <div className={styles.treeBody}>
                  <div className={styles.treeShell} id="PagesTree">
                <GenericTreeView
                  items={pages}
                  closeOnSelect={false}
                  idPropertyName="id"
                  rootNodeKeyValue="#"
                  nodeTypeDefinitions={treeNodeDefinition()}
                  typePropertyName="type"
                  sortBy={TreeItemSort.Unsorted}
                />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.workspaceMain}>
            {selectedPage && (
              <div className={styles.detailPanel}>
                <div className={styles.detailHeader}>
                  <div>
                    <h2 className={styles.detailTitle}>Page Metadata</h2>
                    <p className={styles.detailSummary}>Manage metadata entries for the selected page and keep search-facing information up to date.</p>
                    <div className={styles.selectedInfo}>
                      <span>Selected page</span>
                      <strong>{selectedPage.text}</strong>
                    </div>
                  </div>
                  <div className={styles.detailToolbar}>
                    <Button
                      variant="outline-primary"
                      className="actionButton"
                      title="add meta data"
                      onClick={addNewMetaData}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Add metadata</span>
                    </Button>
                  </div>
                </div>
                <div className={styles.detailBody}>
                {!metaData || metaData.length <= 0 ? (
                  <AppEmptyState
                    icon={faCircleInfo}
                    compact={true}
                    className={styles.emptyNotice}
                    title="No metadata found"
                    message={`No metadata entries have been added for ${selectedPage?.text ?? "this page"} yet.`}
                  />
                ) : (
                  <div className={styles.list}>
                    {metaData.map(md => {
                      const automatic = isAutomaticMetaKey(md.Key);
                      return (
                      <Form
                        id={`MDFORM-${md.Id}`}
                        key={`MDFORM-${md.Id}`}
                        ref={(el: HTMLFormElement) => formRefs.current[`MDFORM-${md.Id}`] = el}
                        className={styles.metaCard}
                      >
                        <Row key={`MD-${md.Id}`} className={styles.metaCardRow}>
                          <Col md={12} xl={4}>
                            <FormLabel>Meta Key</FormLabel>
                            <FormControl
                              type="text"
                              id="Key"
                              name="Key"
                              autoComplete="off"
                              value={md.Key}
                              placeholder="meta value key..."
                              readOnly={automatic}
                              onChange={(e) => handleUpdateMeta(e.target.name, e.target.value, md.Id)}
                            />
                          </Col>
                          <Col md={12} xl={6}>
                            <FormLabel>Meta Value</FormLabel>
                            <FormControl
                              type="text"
                              id="Value"
                              name="Value"
                              autoComplete="off"
                              value={md.Value}
                              placeholder="meta value..."
                              readOnly={automatic}
                              onChange={(e) => handleUpdateMeta(e.target.name, e.target.value, md.Id)}
                            />
                          </Col>
                          <Col md={12} xl={2}>
                            <div className={styles.metaActions}>
                            {!automatic && (
                            <Button
                              variant="success"
                              title="save metadata"
                              className="btn-nomin me-1"
                              onClick={() => saveMetaData(md.Id)}>
                              <FontAwesomeIcon icon={faSave} />
                            </Button>
                            )}
                            <Button
                              variant="danger"
                              title="remove metadata"
                              className="btn-nomin"
                              onClick={() => deleteMetaData(md.Id)}>
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                            </div>
                          </Col>
                          {automatic && (
                            <Col md={12}>
                              <p className={styles.automaticNote}>{automaticMetaNote}</p>
                            </Col>
                          )}
                        </Row>
                      </Form>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            )}

            {!selectedPage && (
              <div className={styles.emptyPanel}>
                <div className={styles.emptyState}>
                  <h2 className={styles.emptyStateTitle}>No page selected</h2>
                  <p className={styles.emptyStateText}>
                    Choose a page from the tree to review and manage the metadata entries attached to it.
                  </p>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {addMetaData && selectedMetaData && (
        <AppModal
          onClose={cancelAddMetaData}
          onDataAction={() => saveMetaData()}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          title="Add metadata"
        >
          <Form id="NEWMETADATA" ref={modalFormRef}>
            <Row>
              <Col md={12}>
                <FormLabel>Meta Key</FormLabel>
                <FormControl
                  id="Key"
                  name="Key"
                  autoFocus={true}
                  autoComplete="off"
                  placeholder="meta key for SEO..."
                  value={selectedMetaData.Key}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel>Meta Value</FormLabel>
                <FormControl
                  id="Value"
                  name="Value"
                  autoFocus={true}
                  autoComplete="off"
                  placeholder="meta value for SEO..."
                  value={selectedMetaData.Value}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>            
          </Form>
        </AppModal>
      )}
    </>
  );
};

export default PageMetaData;
